import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  KDialog,
  KTextInput,
  KCropperWrapper,
  KSelectAutocomplete,
} from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSetState";
import {
  createVehicleModel,
  updateVehicleModel,
} from "../../apiCalls/admin/vehicleModels";
import { getVehicleTypes } from "../../apiCalls/admin/vehicleTypes";
import { setAlert } from "../../redux/actions/actions";
import { getVehicleUrl } from "../../apiCalls/imageUploads/vehicleUploads";
import { RootStateInterface } from "../../interfaces/ReduxInterface";

interface vehicleModelData {
  brand: string;
  created: string;
  id: string;
  images: string[];
  model: string;
  modified: string;
  version: string;
  type: string;
}
interface Props {
  open: boolean;
  handleClose: (boolean) => void;
  data?: vehicleModelData;
}

const NewVehicleModel = ({
  open,
  handleClose,
  data = {
    brand: "",
    created: "",
    id: "",
    images: [""],
    model: "",
    modified: "",
    version: "",
    type: "",
  },
}: Props) => {
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const dispatch = useDispatch();
  const isExistingModel = data.id;

  const [
    { brand, model, version, image, type, vehicleTypes },
    setState,
  ] = useSetState({
    brand: "",
    model: "",
    version: "",
    image: "",
    type: "",
    vehicleTypes: [],
  });

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const setImage = (url): void => {
    setState({ image: url });
  };

  const handleSelectChange = (name, value): void => {
    setState({ [name]: value.label });
  };

  const handleCreateModel = async () => {
    if (isExistingModel) {
      const res = await updateVehicleModel(
        data.id,
        currentCustomer?.shortname,
        brand,
        model,
        version,
        image,
        type,
      );
      if (res.data) {
        handleClose(true);
        dispatch(setAlert("Vehicle Model successfully updated", "success"));
      } else {
        dispatch(setAlert("Error", "error"));
      }
    } else {
      const res = await createVehicleModel(
        currentCustomer?.shortname,
        brand,
        model,
        version,
        image,
        type,
      );
      if (res.data) {
        handleClose(true);
        dispatch(setAlert("New Vehicle Model created", "success"));
      } else {
        dispatch(setAlert("Error", "error"));
      }
    }
  };

  useEffect(() => {
    if (isExistingModel) {
      setState({
        brand: data?.brand,
        model: data?.model,
        version: data?.version,
        image: data?.images[0],
        type: data?.type,
      });
    } else {
      setState({
        brand: "",
        model: "",
        version: "",
        image: "",
        type: "",
      });
    }
  }, [isExistingModel]);

  useEffect(() => {
    getVehicleTypes(currentCustomer.shortname).then((res) => {
      if (res.data) {
        const mappedValues = res.data.data.map((x) => ({
          label: x.name,
        }));
        setState({ vehicleTypes: mappedValues });
      }
    });
  }, []);

  return (
    <KDialog
      open={open}
      title="New Vehicle Model"
      size="xs"
      onClose={() => handleClose(false)}
      primaryCTAonClick={handleCreateModel}
      primaryCTAText={isExistingModel ? "Update" : "Create"}
      primaryCTAColor="primary"
      secondaryCTAonClick={() => handleClose(false)}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={!type}
    >
      <div style={{ minHeight: "400px" }}>
        <KCropperWrapper
          title="Upload Image"
          uploadText="Choose file..."
          helperText="Allowed files are .PNG, .JPG and .JPEG"
          width={1240}
          height={1000}
          border={50}
          borderRadius={0}
          setAvatar={setImage}
          fetchUrl={getVehicleUrl}
          uploadUrl={`${currentCustomer.shortname}/vehicle_models/upload_image`}
          style={{ width: "auto", minWidth: 200, minHeight: 200 }}
          image={image}
        />
        <KSelectAutocomplete
          placeholder="Select Vehicle Type..."
          name="type"
          elements={vehicleTypes}
          onChange={handleSelectChange}
          value={type}
          label="Vehicle Type"
          required
        />
        <KTextInput
          label="Brand"
          placeholder="Audi"
          value={brand}
          name="brand"
          onChange={handleChange}
          width="400px"
        />
        <KTextInput
          label="Model"
          placeholder="XXX"
          value={model}
          name="model"
          onChange={handleChange}
          width="400px"
        />
        <KTextInput
          label="Version"
          placeholder="XXX"
          value={version}
          name="version"
          onChange={handleChange}
          width="400px"
        />
      </div>
    </KDialog>
  );
};

export default NewVehicleModel;
